import { Link } from 'react-router';
import { isOpenLead } from '@mys/contracts/constants';
import { useLeads, STATUS_LABELS, LEAD_STATUSES } from '../lib/leads.js';
import { useAnalytics } from '../lib/analytics.js';
import { useSession } from '../lib/session.js';
import { useCan } from '../lib/permissions.js';
import { formatInr } from '../lib/format.js';
import { ApiError } from '../lib/api.js';

/* ============================================================================
   / — the overview.
   ----------------------------------------------------------------------------
   The first screen of the day, so it answers "what needs me" before it shows
   anything else. Five lead counts and nothing else was what it used to be, and
   it knew nothing about money owed, late projects or a client waiting on a
   change request — the three things that actually cost something.

   It shares one query with /analytics rather than counting anything itself, so
   the two screens cannot disagree about the same morning. The deeper view —
   the six-month trend, GST, month on month — lives there; this is the strip
   you glance at.

   Tiles appear only when they have something to say. A row of zeroes is the
   same problem in a smaller font: it trains you to stop reading the row.
   ========================================================================== */

export function OverviewPage() {
  const { user } = useSession();
  /* The first screen of the day, and the one screen every role lands on — so
     it asks for only what this account may read. An `accounts` login has no
     `leads:view`, and firing that request anyway would greet them with a
     permission error on the home page. */
  const may = useCan();
  const { data, error } = useLeads({}, 1, may('leads:view'));
  const { data: analytics } = useAnalytics(may('analytics:view'));

  const counts = data?.counts ?? {};

  /* DERIVED, not listed. This was `counts.new + counts.contacted +
     counts.quoted` until 2026-10-14 — correct when it was written, and wrong
     from the moment slice 1.5 added `brief` and `preview`: the first sentence
     on the first screen of the day undercounted the pipeline by two whole
     stages, and a lead sitting at "brief received" was not open according to
     the only screen that says the word. Nothing errored, no test failed, and
     the board beside it drew the card correctly the entire time. Same shape as
     the bug `TERMINAL_LEAD_STATUSES` was introduced to end, in the same
     release that introduced it. */
  const open = LEAD_STATUSES.filter(isOpenLead).reduce(
    (total, status) => total + (counts[status] ?? 0),
    0,
  );

  const attention = analytics?.attention;
  const money = analytics?.money;
  const leads = analytics?.leads;

  /* Built as a list so the empty case is `length === 0` rather than four
     conditions repeated in the markup and in the "all clear" test beside it. */
  const urgent = [
    /* First, and above the money. Everything else on this strip is owed by
       somebody over days; this one is owed by the reader inside two hours, and
       it is the only tile here that the public site makes a promise about. */
    leads && leads.overdueNow > 0
      ? {
          to: '/leads?overdue=true',
          label: 'Past two hours',
          value: String(leads.overdueNow),
          note: 'A reply is owed',
        }
      : null,
    attention && attention.lateProjects > 0
      ? {
          to: '/projects?overdue=true',
          label: 'Late projects',
          value: String(attention.lateProjects),
          note: 'The refund clause applies',
        }
      : null,
    money && money.outstandingInr > 0
      ? {
          to: '/invoices?status=due',
          label: 'Outstanding',
          value: formatInr(money.outstandingInr),
          note: `${money.dueInvoices} invoice`,
        }
      : null,
    attention && attention.openEditRequests > 0
      ? {
          to: '/edit-requests',
          label: 'Open change requests',
          value: String(attention.openEditRequests),
          note: 'A client is waiting',
        }
      : null,
    attention && attention.pendingTestimonials > 0
      ? {
          to: '/testimonials',
          label: 'Testimonials pending',
          value: String(attention.pendingTestimonials),
          note: undefined,
        }
      : null,
  ].flatMap((tile) => (tile ? [tile] : []));

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Hello{user?.name ? `, ${user.name}` : ''}</h1>
          {/* Degrades to the bare sentence for a role without
              `analytics:view`, rather than rendering "0 came in today". */}
          <p className="muted">
            {open} leads are open
            {leads ? ` · ${leads.today} came in today, ${leads.thisWeek} this week` : ''}.
          </p>
        </div>
        <Link to="/leads" className="btn">
          Open leads
        </Link>
      </div>

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load the numbers.'}
        </p>
      ) : null}

      {/* Silent when analytics has not arrived or has nothing urgent. A
          skeleton here would flash on every load of the screen you see most. */}
      {urgent.length > 0 ? (
        <section className="card" style={{ marginBottom: 16 }}>
          <h2 className="section-title">Needs attention</h2>
          <div className="stats">
            {urgent.map((tile) => (
              <Link key={tile.to} to={tile.to} className="stat">
                <span className="stat__label">{tile.label}</span>
                <span className="stat__value">{tile.value}</span>
                {tile.note ? <span className="stat__note">{tile.note}</span> : null}
              </Link>
            ))}
          </div>
          <p className="muted" style={{ margin: '12px 0 0', fontSize: 13 }}>
            <Link to="/analytics">See the full numbers</Link>
          </p>
        </section>
      ) : null}

      <h2 className="section-title">Leads</h2>
      <div className="board">
        {LEAD_STATUSES.map((status) => (
          <Link
            key={status}
            to={`/leads?status=${status}`}
            className="board__col"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div className="board__head" style={{ paddingBottom: 4 }}>
              {STATUS_LABELS[status]}
            </div>
            <div style={{ fontFamily: 'var(--display)', fontSize: 28, fontWeight: 700 }}>
              {counts[status] ?? 0}
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}
